import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'

import {
  fetchBalances,
  fetchDelegations,
  fetchNft,
  fetchRedelegations,
  fetchUnbondingDelegations,
} from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
  sumAtomic,
} from '@/helpers'
import { buildCloseNftMsg, CloseNftError } from '@/libs/keplr/messages'
import { invalidateNftScope } from '@/queries/invalidate'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

type Props = {
  open: boolean
  nftAddress: string
  onClose: () => void
}

export default function CloseNftModal({ open, nftAddress, onClose }: Props) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const queryClient = useQueryClient()
  const enabled = Boolean(open && nftAddress)

  const { data: nftData, isLoading: isLoadingNft } = useQuery({
    queryKey: queryKeys.nft.byAddress(nftAddress),
    enabled,
    queryFn: async () => fetchNft(nftAddress),
    staleTime: 15_000,
  })

  const { data: balancesData, isLoading: isLoadingBalances } = useQuery({
    queryKey: queryKeys.bank.balances(nftAddress),
    enabled,
    queryFn: async () => fetchBalances(nftAddress),
    staleTime: 15_000,
  })

  const { data: delegationData, isLoading: isLoadingDelegations } = useQuery({
    queryKey: queryKeys.staking.delegations(nftAddress),
    enabled,
    queryFn: async () => fetchDelegations(nftAddress),
    staleTime: 15_000,
  })

  const { data: unbondingData, isLoading: isLoadingUnbonding } = useQuery({
    queryKey: queryKeys.staking.unbondingDelegations(nftAddress),
    enabled,
    queryFn: async () => fetchUnbondingDelegations(nftAddress),
    staleTime: 15_000,
  })

  const { data: redelegationsData, isLoading: isLoadingRedelegations } =
    useQuery({
      queryKey: queryKeys.staking.redelegations(nftAddress),
      enabled,
      queryFn: async () => fetchRedelegations(nftAddress),
      staleTime: 15_000,
    })

  const isLoading =
    isLoadingNft ||
    isLoadingBalances ||
    isLoadingDelegations ||
    isLoadingUnbonding ||
    isLoadingRedelegations

  const hasStake =
    (delegationData?.delegation_responses ?? []).length > 0 ||
    (unbondingData?.unbonding_responses ?? []).length > 0 ||
    (redelegationsData?.redelegation_responses ?? []).length > 0

  const balanceAtomic = sumAtomic(
    (balancesData?.balances ?? [])
      .filter(
        b => b.denom === bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
      )
      .map(b => b.amount),
  )

  const closeTx = useMutation({
    mutationKey: queryKeys.tx.close(nftAddress),
    mutationFn: async () => {
      if (!address) throw new Error('Wallet not connected')
      if (hasStake) {
        throw new Error('Shard has an active delegation. Undelegate first.')
      }

      const msg = buildCloseNftMsg({ creator: address, address: nftAddress })
      return signAndBroadcast([msg])
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Shard closed',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      invalidateNftScope(queryClient, nftAddress)
      onClose()
    },
    onError: error => {
      ErrorHandler.process(CloseNftError.fromUnknown(error))
    },
  })

  const isSubmitting = closeTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isSubmitting && onClose()}
      title={<span className='text-foreground'>Close Shard</span>}
      content={
        <div className='text-foreground mt-4 flex flex-col gap-4 p-1'>
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>NFT Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(nftAddress, 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={nftAddress} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Owner</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {nftData ? formatAddress(nftData.owner, 20, 15) : '…'}
              </span>
              {nftData && (
                <div className='ml-auto'>
                  <CopyWithTooltip value={nftData.owner} />
                </div>
              )}
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>
              Amount sent to the owner
            </span>
            <div className='bg-input text-foreground flex h-12 items-center rounded-lg px-3 py-2 text-sm'>
              {isLoading ? '…' : `${formatBridgeAtomic(balanceAtomic)} BRIDGE`}
            </div>
          </div>

          {hasStake ? (
            <div className='flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3'>
              <AlertTriangle className='mt-0.5 size-4 shrink-0 text-yellow-500' />
              <span className='text-xs text-yellow-500'>
                Shard has an active delegation, unbonding or redelegation. It
                can be closed only after the owner undelegates and the unbonding
                completes.
              </span>
            </div>
          ) : (
            <div className='flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3'>
              <AlertTriangle className='mt-0.5 size-4 shrink-0 text-red-400' />
              <span className='text-xs text-red-400'>
                The whole Shard balance is sent to the owner without waiting for
                the end of vesting, and the Shard is deleted. This can not be
                undone.
              </span>
            </div>
          )}
        </div>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={isSubmitting || isLoading || hasStake || !nftData}
            onClick={() => closeTx.mutate()}
          >
            {isSubmitting ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Closing…
              </span>
            ) : (
              'Close Shard'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
